import React, {useEffect, useState} from 'react'
import {
    Box,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material"
import DatePickerSelect from "../../../../components/date/date-picker-select"
import MarketToMarketChart, {
    CHART_TYPE_BOX_PLOT,
    CHART_TYPE_BOX_PLOT_SCATTER,
    CHART_TYPE_LINE,
    CHART_TYPE_SCATTER
} from "./market-to-market-chart"
import PropTypes from 'prop-types'
import {MarketToMarketService} from "../../../../services/market-to-market-service"
import SkeletonInputField from "../../../../components/skeleton-input-field"
import dayjs from "dayjs"
import {DATE_FORMAT} from "../../../../utils/constants"
import {generateControlledColor} from "../../../../utils/colors"
import theme from "../../../../utils/theme"
import ToggleButtonSelect from "../../../../components/toggle-button-select"

const janelaDefault = 10
const curvaSpreadDefault = "CORPORATE DI"

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250,
        }
    },
}

const ToggleButtonSx = {
    '&.Mui-selected': {
        backgroundColor: '#fff',
    },
    '&:not(.Mui-selected)': {
        backgroundColor: theme.palette.background.default,
    }
}

const SPREAD_INDICATIVO = "Spreads Indicativos"
const SPREAD_NEGOCIADO = "Spreads Negociados"
const spreadsDefault = []
const tipoEmissaoDefault = "Corporate"

const MarketToMarket = ({empresa}) => {
    const marketToMarketService = new MarketToMarketService()

    const [chartKey, setChartKey] = useState(Math.random() * 10)
    const [grupoEconomico, setGrupoEconomico] = useState(false)
    const [loadingCurvaSpread, setLoadingCurvaSpread] = useState(false)
    const [dateSelectedDate, setSelectedDate] = useState("")
    const [curvaSpreadSelected, setCurvaSpreadSelected] = useState(curvaSpreadDefault)
    const [curvaSpredItems, setCurvaSpredItems] = useState([])
    const [subsetorItems, setSubsetorItems] = useState([])
    const [subsetorSelected, setSubsetorSelected] = useState(null)
    const [janelaNegocios, setJanelaNegocios] = useState(janelaDefault)
    const [chartData, setChartData] = useState([])
    const [allChartData, setAllChartData] = useState([])
    const [spreads, setSpreads] = useState(spreadsDefault)
    const [spreadsSelected, setSpreadSelected] = useState([])
    const [xValue, setXValue] = useState([])
    const [listaCetips, setListaCetips] = useState([])
    const [cetipsSelected, setCetipsSelected] = useState([])
    const [listaCurvas, setListaCurvas] = useState([])
    const [curvasSelected, setCurvasSelected] = useState([])
    const [tipoEmissao, setTipoEmissao] = useState(tipoEmissaoDefault)

    useEffect(() => {
        if (empresa) {
            loadData()
        }
    }, [empresa])

    const loadData = () => {
        const {CPFCNPJ} = empresa

        setLoadingCurvaSpread(true)
        marketToMarketService.loadSetorEmpresa(CPFCNPJ)
            .then((response) => {
                const {body} = response
                const {DataRecente, DescricaoList, TipoEmissao, Subsetor} = body

                setCurvaSpredItems(DescricaoList)
                setSelectedDate(dayjs(DataRecente).format(DATE_FORMAT))
                setCurvaSpreadSelected(DescricaoList[0])
                setTipoEmissao(TipoEmissao)

                let subsetorDefault = null
                if (Subsetor !== null) {
                    const {List, Default} = Subsetor
                    setSubsetorItems(List)
                    setSubsetorSelected(Default)
                    subsetorDefault = Default
                } else {
                    setSubsetorItems([])
                }

                loadGraficoData(DataRecente, DescricaoList[0], janelaNegocios, TipoEmissao, subsetorDefault, grupoEconomico)
            })
            .catch(() => {
                setLoadingCurvaSpread(false)
            })
    }

    const addItemChartData = (data, eixoX) => {
        const cetipsRetornados = [...new Set(data.filter((item) => item.type !== CHART_TYPE_LINE).map((item) => item.label))]
        const curvasRetornadas = [...new Set(data.filter((item) => item.type === CHART_TYPE_LINE).map((item) => item.label))]

        setListaCurvas(curvasRetornadas)
        setListaCetips(cetipsRetornados)
        setCetipsSelected(cetipsRetornados)
        setCurvasSelected(curvasRetornadas)
        setXValue(eixoX)
        setChartData(data)
        setAllChartData(data)

        setChartKey(Math.random() * 10)
    }

    const loadGraficoData = (data, curva, janelaNegocios, tipoEmissao, subsetor, grupoEconomico) => {
        if (!empresa) return
        const {CPFCNPJ} = empresa

        setLoadingCurvaSpread(true)
        marketToMarketService.loadCurvasEmpresa(CPFCNPJ, data, curva, janelaNegocios, tipoEmissao, subsetor, grupoEconomico)
            .then((response) => {
                const {body} = response
                const {curvas, scatters, box_plot} = body

                const line = loadGraficoLineValueData(curvas, 'TaxaCurvas', null, "Rating")
                const scattersChart = loadGraficoScatterValueData(scatters)
                const boxplotChart = loadGraficoBoxPlotData(box_plot)

                const grafico = [...line, ...scattersChart, ...boxplotChart]
                const graficoCores = geraCoresGrafico(grafico)

                const eixoX = calculaEixoX(curvas, scatters, box_plot)
                const spreads = verificaSpreadsDisponiveis(scatters, boxplotChart)

                setSpreads(spreads)
                addItemChartData(graficoCores, eixoX)
            })
            .catch(() => {
                defaultValues()
            })
            .finally(() => {
                setLoadingCurvaSpread(false)
            })
    }

    const defaultValues = () => {
        setCurvaSpreadSelected(curvaSpreadDefault)
        setChartData([])

        setListaCurvas([])
        setListaCetips([])
        setCetipsSelected([])
        setCurvasSelected([])
        setXValue([])
        setChartData([])
        setAllChartData([])
        setSubsetorItems([])
        setSubsetorSelected(null)

        setChartKey(Math.random() * 10)
    }

    const calculaEixoX = (curvas, scatters, boxPlot) => {
        const LIMITE = 5
        const eixoMax = [...new Set(curvas.flatMap(item => item.DurationAno))].sort((a, b) => a - b)

        if (scatters.length === 0 && boxPlot.length === 0) {
            return eixoMax.filter(d => d <= LIMITE)
        }

        return eixoMax
    }

    const geraCoresGrafico = (grafico) => {
        const labelToColor = {}
        const allLabels = [...new Set(grafico.map(item => item.label))]

        allLabels.forEach(label => {
            if (!labelToColor[label]) {
                labelToColor[label] = generateControlledColor(label, Object.entries(labelToColor).map(([k, v]) => ({
                    label: k,
                    color: v
                })))
            }
        })

        return grafico.map(item => ({
            ...item,
            color: labelToColor[item.label]
        }))
    }

    const verificaSpreadsDisponiveis = (scatters, boxplot) => {
        let spreadsSelecionados = []
        if (scatters.length > 0) {
            spreadsSelecionados.push(SPREAD_INDICATIVO)
        }

        if (boxplot.length > 0) {
            spreadsSelecionados.push(SPREAD_NEGOCIADO)
        }

        changeSpreadSelected(spreadsSelecionados.map((item) => item))
        return spreadsSelecionados
    }

    const loadGraficoLineValueData = (data, input, label, text) => {
        if (data.length === 0) return []

        return data.map((item => {
                const {Descricao} = item
                const itemLabel = label || Descricao
                const itemText = item[text] || ''

                return {
                    label: itemLabel,
                    type: "line",
                    text: itemText,
                    data: item[input],
                    tooltip: {
                        title: "Descrição",
                        x: "Duration",
                        y: "Taxa",
                    }
                }
            })
        )
    }

    const loadGraficoScatterValueData = (data) => {
        let items = []
        data.forEach((item) => {
            const {CETIP, DurationAno, SpreadCredito, IndiceLiquidez, Indicador, Rating} = item
            items.push({
                label: CETIP, type: "scatter", data: [{x: DurationAno, y: SpreadCredito}],
                tooltip:
                    `CETIP: ${CETIP ?? 'N/D'} <br>` +
                    `Duration: ${DurationAno ?? 'N/D'}<br>` +
                    `Indicador: ${Indicador ?? 'N/D'} <br>` +
                    `Rating: ${Rating ?? 'N/D'} <br>` +
                    `Spread Crédito: ${SpreadCredito ?? 'N/D'}<br>` +
                    `Indice Liquidez: ${IndiceLiquidez ?? 'N/D'} <br>`
            })
        })

        return items
    }

    const loadGraficoBoxPlotData = (data) => {
        return data.map((item) => {
            const {CETIP, Min, Max, Q1, Q3, Median} = item

            return {
                label: CETIP, type: 'boxplot',
                x: item.DurationAno,
                data: {Min, Max, Q1, Q3, Median},
                tooltip: `CETIP: ${CETIP}<br>` +
                    `Máximo: ${Max ?? ''} <br>` +
                    `Q3: ${Q3 ?? ''}<br>` +
                    `Mediana:${Median ?? ''}<br>` +
                    `Q1: ${Q1 ?? ''}<br>` +
                    `Mínimo: ${Min ?? ''}<br>`
            }
        })
    }

    const onChangeDatePickerDateTime = (value) => {
        const formattedDate = value.format(DATE_FORMAT)
        setSelectedDate(formattedDate)
        loadGraficoData(formattedDate, curvaSpreadSelected, janelaNegocios, tipoEmissao, subsetorSelected, grupoEconomico)
    }

    const onChangeCurvaSpread = (value) => {
        setCurvaSpreadSelected(value)
        setChartData([])
        loadGraficoData(dateSelectedDate, value, janelaNegocios, tipoEmissao, subsetorSelected, grupoEconomico)
    }

    const onChangeSubsetor = (value) => {
        setSubsetorSelected(value)
        setChartData([])
        loadGraficoData(dateSelectedDate, curvaSpreadSelected, janelaNegocios, tipoEmissao, value, grupoEconomico)
    }

    const onBlurJanela = (value) => {
        const janela = value.target.value
        setJanelaNegocios(janela)
        loadGraficoData(dateSelectedDate, curvaSpreadSelected, janela, tipoEmissao, subsetorSelected, grupoEconomico)
    }

    const changeSearchGrupoEconomico = (value) => {
        setGrupoEconomico(value)
        loadGraficoData(dateSelectedDate, curvaSpreadSelected, janelaNegocios, tipoEmissao, subsetorSelected, value)
    }

    const changeCetipsSelected = (value) => {
        setCetipsSelected(typeof value === 'string' ? value.split(',') : value)
        let filteredCurvas = chartData.filter(item => item.type === CHART_TYPE_LINE)
        let filtered = allChartData.filter(item => value.includes(item.label))

        if (!spreadsSelected.includes(SPREAD_NEGOCIADO)) {
            filtered = filtered.filter(item => item.type !== CHART_TYPE_BOX_PLOT && item.type !== CHART_TYPE_BOX_PLOT_SCATTER)
        }

        if (!spreadsSelected.includes(SPREAD_INDICATIVO)) {
            filtered = filtered.filter(item => item.type !== CHART_TYPE_SCATTER)
        }

        setChartData([...filtered, ...filteredCurvas])
    }

    const changeCurvasSelected = (value) => {
        setCurvasSelected(typeof value === 'string' ? value.split(',') : value)

        let filtered = allChartData.filter(item => value.includes(item.label))
        let chartDataFiltered = chartData.filter(item => item.type !== CHART_TYPE_LINE)

        setChartData([...filtered, ...chartDataFiltered])
    }

    const changeSpreadSelected = (value) => {
        setSpreadSelected(typeof value === 'string' ? value.split(',') : value)

        let filtered = allChartData
            .filter(item => item.type === CHART_TYPE_LINE)
            .filter(item => curvasSelected.includes(item.label))

        if (value.includes(SPREAD_INDICATIVO)) {
            filtered = [
                ...filtered,
                ...allChartData
                    .filter(item => item.type === CHART_TYPE_SCATTER)
                    .filter(item => cetipsSelected.includes(item.label))
            ]
        }

        if (value.includes(SPREAD_NEGOCIADO)) {
            filtered = [
                ...filtered,
                ...allChartData
                    .filter(item => item.type === CHART_TYPE_BOX_PLOT || item.type === CHART_TYPE_BOX_PLOT_SCATTER)
                    .filter(item => cetipsSelected.includes(item.label))
            ]
        }

        setChartData(filtered)
    }

    const getCorSelect = (item) => {
        const itemColor = allChartData.filter((chartItem) => chartItem.label === item)
        return itemColor.length > 0 ? itemColor[0].color : "#000000"
    }

    const getDrawSelect = (item) => {
        let boxElement = {}

        if (item === SPREAD_INDICATIVO) {
            boxElement = {
                width: 0,
                height: 0,
                borderLeft: '9px solid transparent',
                borderRight: '9px solid transparent',
                borderBottom: `10px solid ${theme.palette.primary.main}`,
            }
        }

        if (item === SPREAD_NEGOCIADO) {
            boxElement = {
                width: 12,
                height: 12,
                bgcolor: `${theme.palette.primary.main}`,
            }
        }

        return <Box component="span" sx={{...boxElement, marginRight: 1}}/>
    }

    const loadComponente = <Box sx={{minWidth: 200}}><SkeletonInputField key={"loading-data-recente"}/></Box>
    const loadGrid = <Grid item xs={12} md={3} xl={2}>{loadComponente}</Grid>

    return <>
        {/* items de selecao */}
        <Grid container direction="row" spacing={2} alignItems={"center"}>
            <Grid item xs={12} md={3} xl={2}>
                {loadingCurvaSpread ? loadComponente :
                    <FormControl fullWidth>
                        <InputLabel id="curva-spread-label">Curva de Spread</InputLabel>
                        <Select
                            labelId="curva-spread-label"
                            value={curvaSpreadSelected}
                            label="Curva de Spread"
                            onChange={(e) => onChangeCurvaSpread(e.target.value)}
                        >
                            {curvaSpredItems.map((item) => <MenuItem key={`curva-${item}`}
                                                                     value={item}>{item}</MenuItem>)}
                        </Select>
                    </FormControl>}
            </Grid>
            {subsetorItems.length === 0 ? null :
                <Grid item xs={12} md={3} xl={2}>
                    {loadingCurvaSpread ? loadComponente :
                        <FormControl fullWidth>
                            <InputLabel id="subsetor-label">Curva Setorial</InputLabel>
                            <Select
                                labelId="subsetor-label"
                                value={subsetorSelected}
                                label="Curva Setorial"
                                onChange={(e) => onChangeSubsetor(e.target.value)}
                            >
                                {subsetorItems.map((item) => <MenuItem key={`subsetor-${item}`}
                                                                       value={item}>{item}</MenuItem>)}
                            </Select>
                        </FormControl>}
                </Grid>
            }
            <Grid item xs={12} md={"auto"} xl={"auto"}>
                {loadingCurvaSpread ?
                    loadComponente :
                    <DatePickerSelect
                        label="Data Referência"
                        onChange={onChangeDatePickerDateTime}
                        value={dateSelectedDate}
                    />}
            </Grid>
            <Grid item xs={12} md={"auto"} xl={2}>
                {loadingCurvaSpread ?
                    loadComponente :
                    <FormControl fullWidth>
                        <TextField
                            labelId="janela-negocio-label"
                            value={janelaNegocios}
                            type={"number"}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    onBlurJanela(event)
                                }
                            }}
                            onChange={(event) => setJanelaNegocios(event.target.value)}
                            onBlur={onBlurJanela}
                            label="Janela Negócios (DU)"
                        />
                    </FormControl>}
            </Grid>
            {tipoEmissao === tipoEmissaoDefault ?
                <Grid item xs={12} md={"auto"} xl={2}>
                    {loadingCurvaSpread ?
                        loadComponente :
                        <ToggleButtonSelect leftLabel={"Empresa"} rightLabel={"Grupo Econômico"} value={grupoEconomico}
                                            onChange={changeSearchGrupoEconomico}/>
                    }
                </Grid>
                : <></>}
        </Grid>

        <br/>
        <Paper>

            {/* legenda do grafico */}
            {allChartData.length === 0 ? <></> :
                <Box sx={{
                    paddingTop: 4,
                    paddingBottom: 4,
                    paddingLeft: 4,
                    paddingRight: 4
                }}>
                    <Grid container direction="row" spacing={2} alignItems={"flex-end"} justifyContent={"center"}>

                        {loadingCurvaSpread ? loadGrid : <></>}
                        {listaCurvas.length === 0 || loadingCurvaSpread ? <></> :
                            <Grid item xs={12} md={12} xl={"auto"}>
                                <Box display="flex" gap={2}>
                                    <ToggleButtonGroup
                                        size="small"
                                        value={curvasSelected}
                                        onChange={(event, newValue) => changeCurvasSelected(newValue)}
                                        aria-label="Curvas"
                                        sx={{ display: 'flex', flexWrap: 'wrap' }}
                                    >
                                        {listaCurvas.map((item, index) => (
                                            <ToggleButton
                                                key={`${item}-${index}`}
                                                value={item}
                                                sx={ToggleButtonSx}
                                            >
                                                <Box component="span" sx={{
                                                    marginRight: 1,
                                                    bgcolor: getCorSelect(item),
                                                    width: 18,
                                                    height: 4
                                                }}/>
                                                <Typography variant="body2">{item}</Typography>
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                            </Grid>
                        }

                        {loadingCurvaSpread ? loadGrid : <></>}
                        {spreads.length === 0 || loadingCurvaSpread ? <></> :
                            <Grid item xs={12} md={12} xl={"auto"}>
                                <Box display="flex" gap={2}>
                                    <ToggleButtonGroup
                                        size="small"
                                        value={spreadsSelected}
                                        onChange={(event, newValue) => changeSpreadSelected(newValue)}
                                        aria-label="Tipos de Spreads"
                                        sx={{ display: 'flex', flexWrap: 'wrap' }}
                                    >
                                        {spreads.map((spread, index) => (
                                            <ToggleButton
                                                key={`${spread}-${index}`}
                                                value={spread}
                                                sx={ToggleButtonSx}
                                            >
                                                {getDrawSelect(spread)}
                                                <Typography variant="body2">{spread}</Typography>
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Box>
                            </Grid>
                        }

                        {loadingCurvaSpread ? loadGrid : <></>}
                        {listaCetips.length === 0 || loadingCurvaSpread ? <></> :
                            <Grid item xs={12} md={12} xl={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="cetips-label-id">Lista de CETIPs</InputLabel>
                                    <Select
                                        size="small"
                                        labelId="cetips-label-id"
                                        multiple
                                        label="Lista de CETIPs"
                                        value={cetipsSelected}
                                        onChange={(e) => changeCetipsSelected(e.target.value)}
                                        input={<OutlinedInput label="Lista de CETIPs"/>}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={MenuProps}>
                                        {listaCetips.map((cetip, index) => (
                                            <MenuItem key={`${cetip}-${index}`} value={cetip}>
                                                <Checkbox checked={cetipsSelected.includes(cetip)}/>
                                                <Box component="span" sx={{
                                                    marginRight: 1,
                                                    bgcolor: getCorSelect(cetip),
                                                    width: 10,
                                                    height: 10
                                                }}/>
                                                <Typography variant="body2">{cetip}</Typography>
                                            </MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        }
                    </Grid>
                </Box>
            }

            <br />

            <Grid container direction={"row"} spacing={2} alignItems={"center"}>
                <Grid item xs={12} xl={12} md={12}>
                    <MarketToMarketChart
                        key={chartKey}
                        xValue={xValue}
                        data={chartData}
                        loading={loadingCurvaSpread}
                        labelX={"Duration Ano"}
                        labelY={"Spreads a.a."}
                    />
                </Grid>
            </Grid>
        </Paper>
    </>
}

MarketToMarket.propTypes = {
    loading: PropTypes.func, empresa: PropTypes.object.isRequired,
}

export default MarketToMarket